
import React from "react";

export function useInView<T extends HTMLElement = HTMLDivElement>(threshold = 0.2): [React.RefObject<T | null>, boolean] {
    const ref = React.useRef<T>(null);
    const [inView, setInView] = React.useState<boolean>(false);
    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                obs.disconnect();
            }
        }, { threshold });
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// counts from 0 up to `target` once `start` becomes true
export function useCountUp(target: number, start: boolean, duration = 1400) {
    const [value, setValue] = React.useState(0);
    React.useEffect(() => {
        if (!start) return;
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
            setValue(target);
            return;
        }
        let frame = 0;
        const begin = performance.now();
        const tick = (now: number) => {
            const progress = Math.min(1, (now - begin) / duration);
            setValue(Math.round(target * (1 - Math.pow(1 - progress, 3))));
            if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [target, start, duration]);
    return value;
}
